import { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material';

import { Box, AddCircleOutlineIcon, Button } from '@/ui';
import { ToggleAll } from '@/core/components/toggle-all/toggle-all';
import {
  useLayerStoreActions,
  useLayerDisplayState,
  useLayerLegendLayers,
} from '@/core/stores/store-interface-and-intial-values/layer-state';
import { useGeoViewMapId } from '@/core/stores';
import type { TypeLayersViewDisplayState } from './types';
import { logger } from '@/core/utils/logger';
import type { TypeContainerBox } from '@/core/types/global-types';

interface TypeLayersToolbar {
  containerType: TypeContainerBox;
}

export function LayersToolbar({ containerType }: TypeLayersToolbar): JSX.Element {
  // Log
  logger.logTraceRender('components/layers/layers-toolbar');

  // Hooks
  const mapId = useGeoViewMapId();
  const theme = useTheme();
  const { t } = useTranslation<string>();
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const userClickedAdd = useRef(false);

  const layerToolbarStyle = {
    display: 'inline-flex',
    gap: '4px',
    '&>button': { padding: '10px 15px' },
    '& .MuiButton-startIcon': { [theme.breakpoints.down('sm')]: { margin: 0, padding: '0 0.25rem' } },
    '& .MuiButtonGroup-root': { backgroundColor: theme.palette.geoViewColor.bgColor.light[300] },
  };

  // Store
  const displayState = useLayerDisplayState();
  const legendLayers = useLayerLegendLayers();
  const { setDisplayState } = useLayerStoreActions();

  // State
  const lastDisplayState = useRef<TypeLayersViewDisplayState | null>(null);

  /**
   * Handles toolbar button clicks and sets the appropriate display state.
   * For the Add button, it sets a flag to prevent auto-switching back to view mode.
   */
  const handleSetDisplayState = useCallback(
    (displayStateParam: TypeLayersViewDisplayState): void => {
      // If user clicks Add, set the flag
      if (displayStateParam === 'add') {
        userClickedAdd.current = true;
      } else {
        userClickedAdd.current = false;
      }

      setDisplayState(displayStateParam);
    },
    [setDisplayState]
  );

  /**
   * Primary effect for handling display state logic.
   * - Forces 'add' state when no layers exist
   * - Tracks display state changes
   * - Resets flags when transitioning away from 'add' state
   * - Restores focus when user cancels
   */
  useEffect(() => {
    // Always show 'add' panel when there are no layers
    if (legendLayers.length === 0 && displayState !== 'add') {
      setDisplayState('add');
    }

    // Track display state changes to handle transitions
    if (lastDisplayState.current !== displayState) {
      lastDisplayState.current = displayState;

      // Restore focus to Add button when user cancels (before resetting the userClickedAdd flag)
      if (displayState === 'view' && userClickedAdd.current) {
        requestAnimationFrame(() => {
          addButtonRef.current?.focus();
        });
      }

      // Reset the userClickedAdd flag when leaving 'add' state
      if (displayState !== 'add') {
        userClickedAdd.current = false;
      }
    }
  }, [displayState, legendLayers.length, setDisplayState]);

  /**
   * Secondary effect specifically for auto-switching to view mode.
   * Only runs when the layer count changes to avoid race conditions.
   * Auto-switches from 'add' to 'view' only when:
   * 1. Layers exist
   * 2. Current state is 'add'
   * 3. User didn't explicitly click the Add button
   */
  useEffect(() => {
    if (legendLayers.length > 0 && displayState === 'add' && !userClickedAdd.current) {
      setDisplayState('view');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legendLayers.length]); // Only depend on legendLayers.length

  return (
    <Box id={`${mapId}-${containerType}-layers-toolbar`} sx={layerToolbarStyle}>
      <Button
        ref={addButtonRef}
        makeResponsive
        type="text"
        size="small"
        tooltip={t('legend.addLayer')!}
        variant={displayState === 'add' ? 'contained' : 'outlined'}
        startIcon={<AddCircleOutlineIcon sx={{ fontSize: theme.palette.geoViewFontSize.sm }} />}
        onClick={() => handleSetDisplayState('add')}
      >
        {t('legend.addLayer')}
      </Button>
      <ToggleAll source="layers" containerType={containerType} />
    </Box>
  );
}
